import React, { useEffect } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface ButtonConfig {
  text: string;
  onPress: () => void;
  loading?: boolean;
}

interface ConfirmationModalProps {
  visible: boolean;
  title: string;
  message: string;
  type?: 'success' | 'warning' | 'error' | 'question' | 'info';
  iconName?: React.ComponentType<any>;
  showCloseButton?: boolean;
  onClose?: () => void;
  variant?: 'single' | 'two';
  button?: ButtonConfig;
  buttons?: {
    cancel: ButtonConfig;
    confirm: ButtonConfig;
  };
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  visible,
  title,
  message,
  type = 'info',
  iconName,
  showCloseButton,
  onClose,
  variant = 'single',
  button,
  buttons
}) => {
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [visible]);

  // Determine colors and icon based on type
  const getTypeConfig = () => {
    switch (type) {
      case 'success':
        return {
          buttonClass: 'bg-primary-600 hover:bg-primary-700',
          iconClass: 'text-primary-600 dark:text-primary-400',
          bgClass: 'bg-primary-100 dark:bg-primary-900/40',
          defaultIcon: CheckCircleIcon,
        };
      case 'warning':
        return {
          buttonClass: 'bg-warning-600 hover:bg-warning-700',
          iconClass: 'text-warning-600 dark:text-warning-400',
          bgClass: 'bg-warning-100 dark:bg-warning-900/40',
          defaultIcon: ExclamationTriangleIcon,
        };
      case 'error':
        return {
          buttonClass: 'bg-error-600 hover:bg-error-700',
          iconClass: 'text-error-600 dark:text-error-400',
          bgClass: 'bg-error-100 dark:bg-error-900/40',
          defaultIcon: ExclamationCircleIcon,
        };
      case 'question':
        return {
          buttonClass: 'bg-primary-600 hover:bg-primary-700',
          iconClass: 'text-primary-600 dark:text-primary-400',
          bgClass: 'bg-primary-100 dark:bg-primary-900/40',
          defaultIcon: QuestionMarkCircleIcon,
        };
      case 'info':
      default:
        return {
          buttonClass: 'bg-info-600 hover:bg-info-700',
          iconClass: 'text-info-600 dark:text-info-400',
          bgClass: 'bg-info-100 dark:bg-info-900/40',
          defaultIcon: InformationCircleIcon,
        };
    }
  };

  const typeConfig = getTypeConfig();
  const Icon = iconName || typeConfig.defaultIcon;

  if (!visible) return null;

  const handleBackdropClick = () => {
    if (variant === 'single' && button) {
      button.onPress();
    } else if (variant === 'two' && buttons) {
      buttons.cancel.onPress();
    } else if (onClose) {
      onClose();
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else if (variant === 'single' && button) {
      button.onPress();
    } else if (variant === 'two' && buttons) {
      buttons.cancel.onPress();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className={`w-9 h-9 rounded-xl ${typeConfig.bgClass} ${typeConfig.iconClass} grid place-items-center flex-shrink-0`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{title}</h3>
          </div>
          {(showCloseButton || onClose) && (
            <button
              onClick={handleClose}
              className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {message.split('\n').map((line, index) => {
              if (line.startsWith('•')) {
                const [dogName, ...rest] = line.substring(2).split(':');
                if (rest.length > 0) {
                  return (
                    <div key={index} className="text-left mb-2 flex items-start">
                      <span className="text-gray-400 dark:text-gray-500 mr-2 mt-0.5">•</span>
                      <div>
                        <span className="font-semibold text-gray-800 dark:text-gray-200">{dogName}:</span>
                        <span className="text-gray-600 dark:text-gray-300">{rest.join(':')}</span>
                      </div>
                    </div>
                  );
                }
                return (
                  <p key={index} className="text-left mb-2">
                    {line}
                  </p>
                );
              }
              return (
                <p key={index} className={`${index > 0 ? 'mt-3' : ''} ${line.includes('?') ? 'font-semibold text-gray-800 dark:text-gray-200' : ''}`}>
                  {line}
                </p>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        {variant === 'single' && button && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
            <button
              onClick={button.onPress}
              disabled={button.loading}
              className={`px-4 py-2 rounded-lg ${typeConfig.buttonClass} text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              {button.loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                button.text
              )}
            </button>
          </div>
        )}
        {variant === 'two' && buttons && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
            <button
              onClick={buttons.cancel.onPress}
              disabled={buttons.confirm.loading}
              className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 font-semibold text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {buttons.cancel.text}
            </button>
            <button
              onClick={buttons.confirm.onPress}
              disabled={buttons.confirm.loading}
              className={`px-4 py-2 rounded-lg ${typeConfig.buttonClass} text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              {buttons.confirm.loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                buttons.confirm.text
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmationModal;
